// ---------------------------------------------------------------------------
// C4: Purchase Intent Detection — /reputation/social/intent
// ---------------------------------------------------------------------------
// Detect buying signals from social mentions. Auto-create CRM contacts.
// Intent alert cards with signal + source + contact link + DM flow trigger.
// ---------------------------------------------------------------------------
import {
  ShoppingCart,
  User,
  MessageCircle,
  ExternalLink,
  Sparkles,
  AlertCircle,
  Zap,
  Globe,
  DollarSign,
  Search,
  Star,
  GitCompare,
} from "lucide-react";
import { useApi } from "@/hooks/use-api";
import { useNavigate } from "react-router";
import { EmptyState } from "@/components/ui/empty-state";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface IntentSignal {
  id: string;
  platform: string;
  authorName: string;
  authorHandle: string;
  content: string;
  intentType: "price_inquiry" | "dm_request" | "review_search" | "comparison" | "recommendation_ask";
  confidence: number;
  detectedAt: string;
  contactCreated: boolean;
  contactId?: string;
}

interface IntentData {
  signals: IntentSignal[];
  totalDetected: number;
  contactsCreated: number;
  period: string;
}

// ---------------------------------------------------------------------------
// Intent Type Config
// ---------------------------------------------------------------------------

const INTENT_CONFIG: Record<string, { icon: typeof DollarSign; color: string; label: string }> = {
  price_inquiry: { icon: DollarSign, color: "text-[var(--color-success)] bg-[var(--color-success)]/5", label: "Price Inquiry" },
  dm_request: { icon: MessageCircle, color: "text-[var(--color-primary)] bg-[var(--color-primary-light)]", label: "DM Request" },
  review_search: { icon: Search, color: "text-[var(--color-warning)] bg-[var(--color-warning)]/5", label: "Review Search" },
  comparison: { icon: GitCompare, color: "text-[var(--color-ai)] bg-[var(--color-ai-light)]", label: "Comparison" },
  recommendation_ask: { icon: Star, color: "text-[var(--color-ai)] bg-[var(--color-ai)]/5", label: "Recommendation" },
};

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function IntentSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="h-24 animate-pulse rounded-xl bg-muted" />
        ))}
      </div>
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="h-32 animate-pulse rounded-xl bg-muted" style={{ animationDelay: `${i * 50}ms` }} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Intent Alert Card
// ---------------------------------------------------------------------------

function IntentCard({ signal }: { signal: IntentSignal }) {
  const navigate = useNavigate();
  const config = INTENT_CONFIG[signal.intentType] ?? { icon: DollarSign, color: "text-[var(--color-success)] bg-[var(--color-success)]/5", label: "Intent" };
  const Icon = config.icon;

  return (
    <div className="card-hover rounded-xl border border-border bg-background overflow-hidden border-l-4 border-l-[var(--color-accent-action)]">
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${config.color}`}>
              <Icon className="h-3.5 w-3.5" />
              {config.label}
            </span>
            <span className="text-xs text-muted-foreground">
              {signal.confidence}% confidence
            </span>
          </div>
          <span className="text-[10px] text-muted-foreground">
            {new Date(signal.detectedAt).toLocaleString()}
          </span>
        </div>

        <div className="mt-3 flex items-start gap-3">
          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
            <User className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">{signal.authorName}</span>
              <span className="text-xs text-muted-foreground">@{signal.authorHandle}</span>
              <Globe className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground capitalize">{signal.platform}</span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{signal.content}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {signal.contactCreated && signal.contactId && (
              <button
                onClick={() => navigate(`/crm/contacts/${signal.contactId}`)}
                className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted transition-colors"
              >
                <User className="h-3 w-3" />
                View Contact
              </button>
            )}
            <button className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-primary)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--color-primary)]/90 transition-colors">
              <Zap className="h-3 w-3" />
              Trigger DM Flow
            </button>
          </div>
          {signal.contactCreated && (
            <span className="flex items-center gap-1 text-[10px] text-[var(--color-success)]">
              <Sparkles className="h-3 w-3" />
              Auto-created in CRM
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function PurchaseIntent() {
  const { data, isLoading, error } = useApi<IntentData>("", { enabled: false });

  if (isLoading) return <IntentSkeleton />;

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
        <AlertCircle className="h-4 w-4" />
        Failed to load intent signals.
      </div>
    );
  }

  if (!data || data.signals.length === 0) {
    return (
      <EmptyState
        icon={<ShoppingCart className="h-8 w-8" />}
        title="No purchase intent detected yet"
        description="When people ask about pricing, request DMs, or compare services on social media, intent signals will appear here automatically."
        variant="info"
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Purchase Intent</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Buying signals detected from social media — auto-creating leads in CRM.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-border bg-background p-4 shadow-sm">
          <div className="text-sm text-muted-foreground">Signals Detected</div>
          <div className="mt-1 text-2xl font-bold text-foreground tabular-nums">{data.totalDetected}</div>
          <div className="text-xs text-muted-foreground">in {data.period}</div>
        </div>
        <div className="rounded-xl border border-border bg-background p-4 shadow-sm">
          <div className="text-sm text-muted-foreground">Contacts Auto-Created</div>
          <div className="mt-1 text-2xl font-bold text-[var(--color-success)] tabular-nums">{data.contactsCreated}</div>
          <div className="text-xs text-muted-foreground">
            {data.totalDetected > 0 ? Math.round((data.contactsCreated / data.totalDetected) * 100) : 0}% conversion
          </div>
        </div>
        <div className="rounded-xl border border-border bg-background p-4 shadow-sm">
          <div className="text-sm text-muted-foreground">Top Intent Type</div>
          <div className="mt-1 text-lg font-bold text-foreground">
            {data.signals[0] ? INTENT_CONFIG[data.signals[0].intentType]?.label : "--"}
          </div>
        </div>
      </div>

      {/* Signal Feed */}
      <div className="space-y-3">
        {data.signals.map((signal) => (
          <IntentCard key={signal.id} signal={signal} />
        ))}
      </div>
    </div>
  );
}
